import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/cn";
import { ApiError } from "@/api/client";
import { enableMyCompanyScan, probeAtsBoard } from "@/api/companies";
import type { MergedCompanyView } from "@career-ops-amplify/shared";

const ATS_OPTIONS = [
  { value: "greenhouse", label: "Greenhouse" },
  { value: "ashby", label: "Ashby" },
  { value: "lever", label: "Lever" },
  { value: "smartrecruiters", label: "SmartRecruiters" },
  { value: "rippling", label: "Rippling" },
  { value: "workable", label: "Workable" },
  { value: "workday", label: "Workday" },
];

interface EnableScanPanelProps {
  slug: string;
  company: MergedCompanyView;
}

export function EnableScanPanel({ slug, company }: EnableScanPanelProps) {
  const queryClient = useQueryClient();
  const user = company.user;
  const global = company.global;

  const [ats, setAts] = useState(
    user?.ats ?? global?.ats ?? "greenhouse",
  );
  const [boardToken, setBoardToken] = useState(
    user?.boardToken ?? global?.boardToken ?? slug,
  );
  const [probeMsg, setProbeMsg] = useState<string | null>(null);
  const [probeOk, setProbeOk] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const scannableHint =
    ats === "workday"
      ? "Workday token format: tenant|site (e.g. acme|External). Optional third part is wd host number (wd3)."
      : "Board slug from the careers URL — e.g. boards.greenhouse.io/stripe → stripe. Not a paid API key.";

  async function handleProbe() {
    setProbing(true);
    setProbeMsg(null);
    setProbeOk(null);
    setError(null);
    try {
      const res = await probeAtsBoard({ ats, boardToken: boardToken.trim() });
      setProbeOk(res.ok);
      setProbeMsg(
        res.ok
          ? `Board looks reachable${res.jobCount != null ? ` (~${res.jobCount} jobs)` : ""}.`
          : res.message || "Board not reachable with this ATS + token.",
      );
    } catch (err) {
      setProbeOk(false);
      setProbeMsg(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Probe failed",
      );
    } finally {
      setProbing(false);
    }
  }

  async function handleEnable() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await enableMyCompanyScan(slug, {
        name: company.name,
        ats,
        boardToken: boardToken.trim(),
        careersUrl: user?.careersUrl ?? global?.careersUrl ?? global?.careersPage,
        url: user?.url ?? global?.url ?? global?.website,
      });
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      await queryClient.invalidateQueries({ queryKey: ["explore", "sources"] });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to enable scan",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-5">
      <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
        Enable scan
      </h3>
      <p className="mb-4 text-xs text-muted-foreground">{scannableHint}</p>

      <div className="grid gap-3 sm:grid-cols-[140px_1fr_auto]">
        <select
          value={ats}
          onChange={(e) => setAts(e.target.value)}
          className="rounded-md border border-border bg-bg px-2 py-1.5 text-sm"
        >
          {ATS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={boardToken}
          onChange={(e) => setBoardToken(e.target.value)}
          placeholder="Board token / slug"
          className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          onClick={() => void handleProbe()}
          disabled={probing || !boardToken.trim()}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
        >
          {probing ? "Probing…" : "Test board"}
        </button>
      </div>

      {probeMsg && (
        <p
          className={cn(
            "mt-2 text-xs",
            probeOk ? "text-emerald-600" : "text-amber-700 dark:text-amber-300",
          )}
        >
          {probeMsg}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void handleEnable()}
          disabled={saving || !boardToken.trim()}
          className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save & enable for Find Jobs"}
        </button>
        <Link
          to={`/explore?slug=${encodeURIComponent(slug)}&mode=company`}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Open in Find Jobs
        </Link>
        <Link
          to={`/explore?slug=${encodeURIComponent(slug)}&mode=ai`}
          className="text-xs text-brand hover:underline"
        >
          Search the web for roles at {company.name}
        </Link>
      </div>

      {saved && (
        <p className="mt-2 text-xs text-emerald-600">
          Enabled. This employer will appear under My enabled in Find Jobs.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
